/**
 * Item-attached logs (R12) — found documents, the `human_log` channel's
 * structural home.
 *
 * @remarks
 * A log is a physical document, so its definition lives ON its carrier item
 * (`item.log`) — the `containedIn` move again: "a log is somewhere" is true
 * by construction, so there is no dangling reference to validate, no orphan
 * rule to write, and no two-places-to-edit drift. A second pack's terminal
 * screen is just a non-portable item with a `log` field; the carrier varies,
 * the shape does not.
 *
 * Reading IS inspecting (`read` has aliased `inspect` in the standard
 * vocabulary from the start): every inspect speaks the document, the FIRST
 * inspect marks `narrative.readLogIds`, and the `log_read` condition lets
 * triggers react the same turn. The speaking order is the three-beat arc the
 * items schema has promised since R4 — `description` (the thing) → the log's
 * own `narratives` (the human voice) → `inspectNarratives` (EURYDICE's
 * comment AFTER the read).
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { logIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';
import { narrativeLineSchema } from './transcript.js';

/** One found document, authored on the item that carries it — see the module remarks. */
export const itemLogSchema = z
	.strictObject({
		logId: logIdSchema,
		/** The document's own lines, in reading order — typically `human_log`-sourced. */
		narratives: z.array(narrativeLineSchema).min(1).readonly(),
		/**
		 * What the document IS, as a title (J1/D78, Mike's ruling with Calder's
		 * field study): the journal shelf's heading. Optional — absent, the
		 * shelf falls back to the carrier item's name, so every pre-D78 pack
		 * reads exactly as it did. A name, not a narrative line: plain string,
		 * like `item.name`.
		 */
		title: nonBlankStringSchema.optional(),
	})
	.readonly();

/** One found document — see {@link itemLogSchema}. */
export type ItemLog = z.infer<typeof itemLogSchema>;
